// FILE: PostWriter.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import TextAlign from "@tiptap/extension-text-align";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { createLowlight, common } from "lowlight";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";

// (Optional) You can register additional highlight.js languages if you need more than "common"
// import js from "highlight.js/lib/languages/javascript";
// const lowlight = createLowlight();
// lowlight.registerLanguage("javascript", js);
const lowlight = createLowlight(common);

// -----------------------------
// Small helpers
// -----------------------------
function estimateReadingTime(text) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const minutes = Math.max(1, Math.round(words.length / 200));
  return { minutes, words: words.length };
}

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");
}

function TB({ active, onClick, title, children, disabled }) {
  return (
    <button type="button" title={title} className={`tb ${active ? "active" : ""}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function TagsInput({ tags, onChange }) {
  const [value, setValue] = useState("");
  function add(raw) {
    const t = raw.trim().toLowerCase();
    if (!t) return;
    if (!tags.includes(t)) onChange([...tags, t]);
    setValue("");
  }
  return (
    <div className="bp-row">
      <label className="bp-label">Tags</label>
      <div className="bp-tags">
        {tags.map((t) => (
          <span key={t} className="bp-tag">
            {t}
            <button className="bp-tag-btn" onClick={() => onChange(tags.filter((x) => x !== t))}>×</button>
          </span>
        ))}
        <input
          placeholder="Add tag and press Enter"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "," || e.key === "Enter") {
              e.preventDefault();
              add(value.replace(/,$/, ""));
            } else if (e.key === "Backspace" && value === "" && tags.length) {
              onChange(tags.slice(0, -1));
            }
          }}
        />
      </div>
    </div>
  );
}

// Simple uploader — expects /api/uploads -> { url }
async function uploadImage(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/uploads", { method: "POST", body: form });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.url;
}

export default function PostWriter({ initialData, onSaved }) {
  // Meta + form
  const [title, setTitle] = useState(initialData?.title || "");
  const [slug, setSlug] = useState(initialData?.slug || "");
  const [excerpt, setExcerpt] = useState(initialData?.excerpt || "");
  const [coverImage, setCoverImage] = useState(initialData?.coverImage || "");
  const [tags, setTags] = useState(initialData?.tags || []);
  const [status, setStatus] = useState(initialData?.status || "draft");
  const [publishAt, setPublishAt] = useState(initialData?.publishedAt || "");
  const [metaTitle, setMetaTitle] = useState(initialData?.metaTitle || "");
  const [metaDescription, setMetaDescription] = useState(initialData?.metaDescription || "");
  const [previewOpen, setPreviewOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Underline,
      Link.configure({ openOnClick: true, autolink: true, HTMLAttributes: { rel: "noopener nofollow" } }),
      Image.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder: "Write your post… / type '/' for commands" }),
      CharacterCount.configure({ limit: 20000 }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      CodeBlockLowlight.configure({ lowlight }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialData?.contentJson ?? "",
    editorProps: {
      attributes: { class: "bp-editor" },
      handlePaste(view, event) {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const it of items) {
          if (it.kind === "file") {
            const file = it.getAsFile();
            if (file) {
              uploadImage(file)
                .then((url) => {
                  if (!view || !url) return;
                  editor?.chain().focus().setImage({ src: url }).run();
                })
                .catch(() => alert("Image upload failed"));
              return true;
            }
          }
        }
        return false;
      },
    },
    autofocus: "end",
  });

  // Auto slug
  useEffect(() => {
    if (!title) return;
    const s = slugify(title);
    if (!slug || slugify(slug) === slugify(initialData?.title || "")) setSlug(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  // Autosave
  const autosaveKey = useMemo(() => `blog-writer-autosave-${initialData?._id ?? "new"}`, [initialData?._id]);
  useEffect(() => {
    const int = setInterval(() => {
      const payload = buildPayload(false);
      localStorage.setItem(autosaveKey, JSON.stringify(payload));
    }, 2000);
    return () => clearInterval(int);
  }, [title, slug, excerpt, coverImage, tags, status, publishAt, metaTitle, metaDescription, editor, autosaveKey]);

  // Restore autosave (new post only)
  useEffect(() => {
    const raw = localStorage.getItem(autosaveKey);
    if (raw) {
      try {
        const data = JSON.parse(raw);
        if (!initialData) {
          setTitle(data.title || "");
          setSlug(data.slug || "");
          setExcerpt(data.excerpt || "");
          setCoverImage(data.coverImage || "");
          setTags(data.tags || []);
          setStatus(data.status || "draft");
          setPublishAt(data.publishedAt || "");
          setMetaTitle(data.metaTitle || "");
          setMetaDescription(data.metaDescription || "");
          if (data.contentJson) editor?.commands.setContent(data.contentJson);
        }
      } catch {}
    }
  }, [editor, autosaveKey, initialData]);

  const getHtml = () => editor?.getHTML() ?? "";
  const getJson = () => editor?.getJSON() ?? {};
  const { minutes, words } = useMemo(() => estimateReadingTime(getHtml().replace(/<[^>]+>/g, " ")), [editor?.state]);

  function buildPayload(includeId = true) {
    return {
      ...(includeId && initialData?._id ? { _id: initialData._id } : {}),
      title,
      slug,
      excerpt,
      coverImage,
      tags,
      status,
      publishedAt: publishAt || null,
      metaTitle: metaTitle || title,
      metaDescription,
      contentHtml: getHtml(),
      contentJson: getJson(),
    };
  }

  const savePost = useCallback(async () => {
    const payload = buildPayload();
    const res = await fetch(`/api/posts${initialData?._id ? `/${initialData._id}` : ""}`, {
      method: initialData?._id ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      alert("Save failed. Please try again.");
      return;
    }
    const saved = await res.json();
    localStorage.removeItem(autosaveKey);
    onSaved && onSaved(saved);
    if (saved.status === "published") setPreviewOpen(true);
  }, [title, slug, excerpt, coverImage, tags, status, publishAt, metaTitle, metaDescription, editor]);

  const fileInputRef = useRef(null);
  const insertImageFromFile = async (file) => {
    try {
      const url = await uploadImage(file);
      editor?.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch {
      alert("Image upload failed");
    }
  };

  return (
    <div className="post-writer-page">
      <div className="bp-head">
        <div className="post-writer-header">
            <h1>Post Writer</h1>
            <p className="videos-subtitle">
                Please fill out the sections below to make a post!
            </p>
        </div>
        <div className="bp-right">
          <button className="bp-btn" onClick={() => setPreviewOpen(true)}>Preview</button>
          <button className="bp-btn primary" onClick={savePost}>Save</button>
        </div>
      </div>

      <div className="bp-grid">
        {/* Main */}
        <div>
          <div className="bp-card">
            <div className="bp-row">
              <label className="bp-label">Title</label>
              <input className="bp-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Post title" />
            </div>
            <div className="bp-row">
              <label className="bp-label">Slug</label>
              <input className="bp-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="my-awesome-post" />
            </div>
            <div className="bp-row">
              <label className="bp-label">Excerpt</label>
              <textarea className="bp-textarea" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Short summary for lists and social previews." />
            </div>

            <div className="bp-row">
              <label className="bp-label">Cover Image</label>
              <div className="bp-flex">
                <input className="bp-input" value={coverImage} onChange={(e) => setCoverImage(e.target.value)} placeholder="https://…" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) insertImageFromFile(f);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                />
                <button className="bp-btn" onClick={() => fileInputRef.current?.click()}>Upload</button>
              </div>
            </div>

            <div className="bp-row">
              <label className="bp-label">Content</label>

              {/* Toolbar */}
              <div className="bp-toolbar">
                <TB title="Bold"   active={!!editor && editor.isActive("bold")}      onClick={() => editor && editor.chain().focus().toggleBold().run()} disabled={!editor}>B</TB>
                <TB title="Italic" active={!!editor && editor.isActive("italic")}    onClick={() => editor && editor.chain().focus().toggleItalic().run()} disabled={!editor}><i>I</i></TB>
                <TB title="Underline" active={!!editor && editor.isActive("underline")} onClick={() => editor && editor.chain().focus().toggleUnderline().run()} disabled={!editor}><u>U</u></TB>
                <TB title="Strike" active={!!editor && editor.isActive("strike")}    onClick={() => editor && editor.chain().focus().toggleStrike().run()} disabled={!editor}><s>S</s></TB>
                <TB title="Code"   active={!!editor && editor.isActive("code")}      onClick={() => editor && editor.chain().focus().toggleCode().run()} disabled={!editor}>`</TB>

                <TB title="H1" active={!!editor && editor.isActive("heading", { level: 1 })} onClick={() => editor && editor.chain().focus().toggleHeading({ level: 1 }).run()} disabled={!editor}>H1</TB>
                <TB title="H2" active={!!editor && editor.isActive("heading", { level: 2 })} onClick={() => editor && editor.chain().focus().toggleHeading({ level: 2 }).run()} disabled={!editor}>H2</TB>
                <TB title="H3" active={!!editor && editor.isActive("heading", { level: 3 })} onClick={() => editor && editor.chain().focus().toggleHeading({ level: 3 }).run()} disabled={!editor}>H3</TB>

                <TB title="• List" active={!!editor && editor.isActive("bulletList")} onClick={() => editor && editor.chain().focus().toggleBulletList().run()} disabled={!editor}>• List</TB>
                <TB title="1. List" active={!!editor && editor.isActive("orderedList")} onClick={() => editor && editor.chain().focus().toggleOrderedList().run()} disabled={!editor}>1. List</TB>
                <TB title="Quote" active={!!editor && editor.isActive("blockquote")} onClick={() => editor && editor.chain().focus().toggleBlockquote().run()} disabled={!editor}>❝</TB>
                <TB title="Code Block" active={!!editor && editor.isActive("codeBlock")} onClick={() => editor && editor.chain().focus().toggleCodeBlock().run()} disabled={!editor}>pre</TB>

                <TB title="Align Left"   active={!!editor && editor.isActive({ textAlign: "left" })}   onClick={() => editor && editor.chain().focus().setTextAlign("left").run()} disabled={!editor}>⟸</TB>
                <TB title="Align Center" active={!!editor && editor.isActive({ textAlign: "center" })} onClick={() => editor && editor.chain().focus().setTextAlign("center").run()} disabled={!editor}>⟷</TB>
                <TB title="Align Right"  active={!!editor && editor.isActive({ textAlign: "right" })}  onClick={() => editor && editor.chain().focus().setTextAlign("right").run()} disabled={!editor}>⟹</TB>

                <TB
                  title="Insert Link"
                  onClick={() => {
                    if (!editor) return;
                    const prev = editor.getAttributes("link").href || "";
                    const url = window.prompt("URL", prev);
                    if (url === null) return;
                    if (url === "") editor.chain().focus().extendMarkRange("link").unsetLink().run();
                    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                  }}
                  disabled={!editor}
                >🔗</TB>

                <TB title="Insert Image" onClick={() => fileInputRef.current?.click()} disabled={!editor}>🖼️</TB>
                <TB title="Insert Table" onClick={() => editor && editor.chain().focus().insertTable({ rows:3, cols:3, withHeaderRow:true }).run()} disabled={!editor}>▦</TB>

                <TB title="Clear" onClick={() => editor && editor.chain().focus().unsetAllMarks().clearNodes().run()} disabled={!editor}>⌫</TB>
                <TB title="Undo"  onClick={() => editor && editor.chain().focus().undo().run()} disabled={!editor}>↶</TB>
                <TB title="Redo"  onClick={() => editor && editor.chain().focus().redo().run()} disabled={!editor}>↷</TB>
              </div>

              <EditorContent editor={editor} />
              <div className="bp-meta">
                <div>Words: {words} • ~{minutes} min read</div>
                <div>{editor?.storage.characterCount?.characters() ?? 0}/20000 chars</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="bp-card">
            <div className="bp-row">
              <label className="bp-label">Status</label>
              <div className="bp-flex" style={{ justifyContent: "space-between" }}>
                <span>Publish now?</span>
                <input type="checkbox" checked={status === "published"} onChange={(e) => setStatus(e.target.checked ? "published" : "draft")} />
              </div>
            </div>
            <div className="bp-row">
              <label className="bp-label">Publish at (optional)</label>
              <input className="bp-input" type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} />
            </div>
          </div>

          <div className="bp-card">
            <TagsInput tags={tags} onChange={setTags} />
          </div>

          <div className="bp-card">
            <div className="bp-row">
              <label className="bp-label">Meta Title</label>
              <input className="bp-input" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder="Overrides <title>" />
            </div>
            <div className="bp-row">
              <label className="bp-label">Meta Description</label>
              <textarea className="bp-textarea" value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} placeholder="Description for search/social" />
            </div>
          </div>

          <div className="bp-card" style={{ fontSize: 12, color: "var(--muted)" }}>
            <p>Shortcuts: Ctrl/⌘ + B/I/U, Z/Y. Drag & drop images into the editor to upload.</p>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div className="bp-modal" onClick={() => setPreviewOpen(false)}>
          <div className="bp-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="bp-flex" style={{ justifyContent: "space-between", marginBottom: 8 }}>
              <strong>Preview</strong>
              <button className="bp-btn" onClick={() => setPreviewOpen(false)}>Close</button>
            </div>
            <article>
              <h1 style={{ marginTop: 0 }}>{title}</h1>
              {coverImage ? <img src={coverImage} alt="Cover" style={{ width: "100%", borderRadius: 10 }} /> : null}
              <div dangerouslySetInnerHTML={{ __html: getHtml() }} />
            </article>
          </div>
        </div>
      )}
    </div>
  );
}
